import { compact, first, isEmpty } from 'lodash';

import {
  INCOME_BENEFICIARIES_SUBFORM,
  LEAD_BENEFICIARY_SUBFORM,
  SURVIVING_SPOUSE_SENTINEL,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import {
  AccessParameterRadioGroupKind,
  BeneficiaryFormAccessParameter,
  BeneficiaryFormAccessParameterAgeParam,
  BeneficiaryFormAccessParameters,
  BeneficiaryFormScheduledDistributions,
  DistributionType,
  EntityBeneficiariesFormBeneficiary,
  EntityBeneficiaryAccessFrequencies,
  EntityBeneficiaryAccessFrequency,
} from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.types';
import { parseStringToInteger } from '@/modules/entities/gratTrusts/TermsSubform/utils/mutationInputUtils';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  AccessParameterFrequency,
  AccessParameterKind,
  AugmentedCreateAccessAgeParameterInput,
  AugmentedCreateAccessParameterInput,
  AugmentedCreateBeneficiaryInput,
  AugmentedCreateScheduledDistributionInput,
  BeneficiaryKind,
  BeneficiaryPowerOfAppointmentPower,
  CreateScheduledDistributionInput,
  ScheduledDistributionFrequency,
  ScheduledDistributionKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { SubformsCombinedType } from '../../EntitySubforms.types';
import { getRelatedModelInputProperties } from './common.utils';

export function getCharitablePrimaryBeneficiary(
  entityType: EntityType,
  formInput: Partial<SubformsCombinedType>
): EntityBeneficiariesFormBeneficiary | undefined {
  const validCharitableEntities: EntityType[] = ['crt', 'clt'];

  if (!validCharitableEntities.includes(entityType)) {
    throw new Error('Invalid entity type, not a charitable entity');
  }

  const isCrt = entityType === 'crt';

  return isCrt
    ? first(formInput[INCOME_BENEFICIARIES_SUBFORM]?.incomeBeneficiaries)
    : formInput[LEAD_BENEFICIARY_SUBFORM]?.leadBeneficiary;
}

export function getCharitablePrimaryBeneficiaries(
  entityType: EntityType,
  formInput: Partial<SubformsCombinedType>
): EntityBeneficiariesFormBeneficiary[] {
  const validCharitableEntities: EntityType[] = ['crt', 'clt'];

  if (!validCharitableEntities.includes(entityType)) {
    throw new Error('Invalid entity type, not a charitable entity');
  }

  const isCrt = entityType === 'crt';

  if (isCrt) {
    return formInput[INCOME_BENEFICIARIES_SUBFORM]?.incomeBeneficiaries ?? [];
  }

  const leadBeneficiary = formInput[LEAD_BENEFICIARY_SUBFORM]?.leadBeneficiary;
  return leadBeneficiary ? [leadBeneficiary] : [];
}

export function makeCreateBeneficiaryInputs(
  beneficiaries: EntityBeneficiariesFormBeneficiary[],
  householdId: string
): AugmentedCreateBeneficiaryInput[] {
  return compact(
    beneficiaries.map((beneficiary) => {
      if (!beneficiary.beneficiaryId) {
        return null;
      }

      const beneficiaryId = beneficiary.beneficiaryId;
      const beneficiaryLevel = beneficiary.level || undefined;

      // For SURVIVING_SPOUSE, we don't link to a specific person - it's a dynamic placeholder
      // that resolves to the surviving spouse at runtime. We set the kind explicitly and
      // don't populate any of the ID fields (entityID, individualID, etc.).
      const isSurvivingSpouse = beneficiaryId === SURVIVING_SPOUSE_SENTINEL;
      const beneficiaryIds = isSurvivingSpouse
        ? {}
        : getRelatedModelInputProperties(beneficiaryId);

      const scheduledDistributionCreateInputs = getScheduledDistributions({
        accessParameterKind:
          beneficiary.beneficiaryFormAccessParameters?._accessParameterKind,
        accessParameters: beneficiary.beneficiaryFormAccessParameters,
        scheduledDistributions:
          beneficiary.beneficiaryFormScheduledDistributions,
        householdId,
      });

      const accessParameterCreateInputs = getAccessParameters(
        beneficiary.beneficiaryFormAccessParameters,
        householdId
      );
      const hasPowerOfAppointment =
        beneficiary.beneficiaryFormPowerOfAppointment?._hasPowerOfAppointment &&
        beneficiary.beneficiaryFormPowerOfAppointment?.power;

      const powerIsGeneral =
        beneficiary.beneficiaryFormPowerOfAppointment?.power ===
        BeneficiaryPowerOfAppointmentPower.General;

      return {
        create: {
          notes: beneficiary.notes,
          level: beneficiaryLevel,
          householdID: householdId,
          ...beneficiaryIds,
          // Set kind explicitly for SURVIVING_SPOUSE; otherwise let the backend infer from IDs.
          // Only include the kind field when it's actually needed to avoid schema validation issues.
          ...(isSurvivingSpouse
            ? { kind: BeneficiaryKind.SurvivingSpouse }
            : {}),
        },
        withScheduledDistributions: scheduledDistributionCreateInputs,
        withAccessParameters: accessParameterCreateInputs,
        withPowerOfAppointment: hasPowerOfAppointment
          ? {
              create: {
                householdID: householdId,
                power:
                  beneficiary.beneficiaryFormPowerOfAppointment?.power ||
                  undefined,
                powerOtherNote: powerIsGeneral
                  ? undefined
                  : beneficiary.beneficiaryFormPowerOfAppointment
                      ?.powerOtherNote,
              },
            }
          : undefined,
      };
    })
  );
}

function frequencyIsScheduledDistributionFrequency(
  frequency: EntityBeneficiaryAccessFrequency | undefined | ''
): frequency is ScheduledDistributionFrequency {
  if (!frequency) return false;

  return Object.values(ScheduledDistributionFrequency).includes(
    frequency as ScheduledDistributionFrequency
  );
}

function getScheduledAgeParameters(
  ageParam: BeneficiaryFormAccessParameterAgeParam | undefined
) {
  let ageRequirementStart: number | null | undefined = undefined;
  let ageRequirementEnd: number | null | undefined = undefined;

  switch (ageParam?._ageRequirementKind) {
    case 'upon':
      ageRequirementStart = parseStringToInteger(
        ageParam.ageRequirementStart ?? ''
      );
      break;
    case 'until':
      ageRequirementEnd = parseStringToInteger(
        ageParam.ageRequirementEnd ?? ''
      );
      break;
    case 'between':
      ageRequirementStart = parseStringToInteger(
        ageParam.ageRequirementStart ?? ''
      );
      ageRequirementEnd = parseStringToInteger(
        ageParam.ageRequirementEnd ?? ''
      );
      break;
  }
  return {
    ageRequirementEnd,
    ageRequirementStart,
  };
}

interface GetScheduledDistributionsInput {
  accessParameterKind: AccessParameterRadioGroupKind | undefined;
  accessParameters: BeneficiaryFormAccessParameters | undefined;
  scheduledDistributions: BeneficiaryFormScheduledDistributions | undefined;
  householdId: string;
}

function getScheduledDistributions({
  accessParameters,
  scheduledDistributions,
  accessParameterKind,
  householdId,
}: GetScheduledDistributionsInput): AugmentedCreateScheduledDistributionInput[] {
  if (accessParameterKind === AccessParameterRadioGroupKind.Full) {
    if (!scheduledDistributions) return [];

    if (!scheduledDistributions._hasScheduledDistributions) return [];

    return scheduledDistributions.scheduledDistributions.map<AugmentedCreateScheduledDistributionInput>(
      (sd) => {
        const kind: ScheduledDistributionKind = sd.scheduledDistribution.kind;
        const ageParams: {
          ageRequirementStart: number | null | undefined;
          ageRequirementEnd: number | null | undefined;
        } = {
          ageRequirementStart: undefined,
          ageRequirementEnd: undefined,
        };
        switch (sd._ageRequirementKind) {
          case 'upon':
            ageParams.ageRequirementStart = sd._hasAgeParams
              ? parseStringToInteger(
                  sd.scheduledDistribution.ageRequirementStart ?? ''
                )
              : undefined;
            break;
          case 'until':
            ageParams.ageRequirementEnd = sd._hasAgeParams
              ? parseStringToInteger(
                  sd.scheduledDistribution.ageRequirementEnd ?? ''
                )
              : undefined;
            break;
          case 'between':
            ageParams.ageRequirementStart = sd._hasAgeParams
              ? parseStringToInteger(
                  sd.scheduledDistribution.ageRequirementStart ?? ''
                )
              : undefined;
            ageParams.ageRequirementEnd = sd._hasAgeParams
              ? parseStringToInteger(
                  sd.scheduledDistribution.ageRequirementEnd ?? ''
                )
              : undefined;
            break;
        }
        let frequency: CreateScheduledDistributionInput['frequency'] =
          undefined;

        if (
          kind !== ScheduledDistributionKind.AllIncome &&
          sd.scheduledDistribution.frequency
        ) {
          frequency = sd.scheduledDistribution.frequency;
        }
        const output: AugmentedCreateScheduledDistributionInput = {
          create: {
            ...ageParams,
            amount:
              kind === ScheduledDistributionKind.Amount
                ? sd.scheduledDistribution.amount
                : undefined,
            frequency,
            growByInflation:
              kind === ScheduledDistributionKind.Amount
                ? (sd.scheduledDistribution.growByInflation ?? false)
                : undefined,
            kind: sd.scheduledDistribution.kind,
            percentage: [
              ScheduledDistributionKind.Percentage,
              ScheduledDistributionKind.AllIncome,
            ].includes(kind)
              ? sd.scheduledDistribution.percentage
              : undefined,
            scheduledDistributionNotes:
              sd.scheduledDistribution.scheduledDistributionNotes,
            householdID: householdId,
          },
        };

        return output;
      }
    );
  }

  if (!accessParameters) return [];

  const output: AugmentedCreateScheduledDistributionInput[] = compact(
    accessParameters.amountsOrPercentages
  )
    .filter((ap) => ap.distributionType === DistributionType.Scheduled)
    .map<AugmentedCreateScheduledDistributionInput>((ap) => {
      const kind =
        ap.kind === AccessParameterKind.Amount
          ? ScheduledDistributionKind.Amount
          : ScheduledDistributionKind.Percentage;

      let frequency: ScheduledDistributionFrequency | undefined = undefined;
      if (frequencyIsScheduledDistributionFrequency(ap.frequency)) {
        frequency = ap.frequency;
      }

      const ageParam =
        (ap._hasAgeParams ? first(ap.ageParams) : undefined) ?? undefined;
      const { ageRequirementEnd, ageRequirementStart } =
        getScheduledAgeParameters(ageParam);

      const sd: AugmentedCreateScheduledDistributionInput = {
        create: {
          householdID: householdId,
          kind,
          amount:
            kind === ScheduledDistributionKind.Amount ? ap.amount : undefined,
          frequency,
          growByInflation:
            kind === ScheduledDistributionKind.Amount
              ? (ap.growByInflation ?? false)
              : undefined,
          percentage:
            kind === ScheduledDistributionKind.Percentage
              ? ap.percentage
              : undefined,
          ageRequirementStart,
          ageRequirementEnd,
        },
      };

      return sd;
    });

  // also add the all trust income as scheduled param if necessary
  const scheduledTrustIncomeEnabled =
    accessParameters.checkboxes[AccessParameterKind.AllTrustIncome];
  const allTrustIncomeParam =
    accessParameters.values[AccessParameterKind.AllTrustIncome];

  if (
    scheduledTrustIncomeEnabled &&
    allTrustIncomeParam &&
    allTrustIncomeParam.distributionType === DistributionType.Scheduled
  ) {
    const ageParam =
      (allTrustIncomeParam._hasAgeParams
        ? first(allTrustIncomeParam.ageParams)
        : undefined) ?? undefined;
    const { ageRequirementEnd, ageRequirementStart } =
      getScheduledAgeParameters(ageParam);
    const create: CreateScheduledDistributionInput = {
      kind: ScheduledDistributionKind.AllIncome,
      householdID: householdId,
      percentage: allTrustIncomeParam.percentage,
      ageRequirementStart,
      ageRequirementEnd,
    };
    output.push({
      create,
    });
  }

  return output;
}

function getAccessParameters(
  beneficiaryFormAccessParameters: BeneficiaryFormAccessParameters | undefined,
  householdId: string
): AugmentedCreateAccessParameterInput[] {
  if (!beneficiaryFormAccessParameters) return [];

  const accessParameterKind =
    beneficiaryFormAccessParameters._accessParameterKind;

  const accessParametersAsList: BeneficiaryFormAccessParameter[] = [];
  if (accessParameterKind === AccessParameterRadioGroupKind.Full) {
    accessParametersAsList.push({
      kind: AccessParameterKind.Full,
      _hasFrequency: false,
      _hasAmountOrPercentage: false,
      frequency: undefined,
      _hasAgeParams: false,
      ageParams: [],
      amount: undefined,
      percentage: undefined,
      distributionType: DistributionType.Discretionary,
    });
  }

  if (accessParameterKind === AccessParameterRadioGroupKind.Partial) {
    compact(beneficiaryFormAccessParameters.amountsOrPercentages)
      .filter((ap) => ap.distributionType !== DistributionType.Scheduled)
      .forEach((ap: BeneficiaryFormAccessParameter) => {
        accessParametersAsList.push(ap);
      });

    const { checkboxes, values } = beneficiaryFormAccessParameters;

    const includeKeys: AccessParameterKind[] = (
      Object.keys(checkboxes) as AccessParameterKind[]
    ).filter((key) => checkboxes[key] === true);

    (
      Object.entries(values) as [
        AccessParameterKind,
        BeneficiaryFormAccessParameter,
      ][]
    ).forEach(([key, value]) => {
      if (
        includeKeys.includes(key) &&
        value.distributionType === DistributionType.Discretionary
      ) {
        accessParametersAsList.push({
          ...value,
          kind: key,
        });
      }
    });
  }

  const accessParameterCreateInputs: AugmentedCreateAccessParameterInput[] =
    compact(accessParametersAsList).map(
      (ap: BeneficiaryFormAccessParameter) => {
        const ageParams: AugmentedCreateAccessAgeParameterInput[] =
          getAgeParameters(ap, householdId);

        let frequency: AccessParameterFrequency | undefined = undefined;
        if (
          ap.frequency &&
          ap.frequency !== EntityBeneficiaryAccessFrequencies.Ongoing &&
          // we don't record frequency for all trust income
          ap.kind !== AccessParameterKind.AllTrustIncome
        ) {
          // this should always be of type AccessParameterFrequency in this case, because we're filtering
          // out scheduled distributions above
          frequency = ap.frequency as AccessParameterFrequency;
        }

        return {
          create: {
            kind: ap.kind,
            amount: ap.amount,
            percentage: ap.percentage,
            frequency,
            growByInflation:
              ap.kind === AccessParameterKind.Amount
                ? (ap.growByInflation ?? false)
                : undefined,
            householdID: householdId,
          },
          withAccessAgeParameters: ageParams,
        };
      }
    );
  return accessParameterCreateInputs;
}

function getAgeParameters(
  accessParameter: BeneficiaryFormAccessParameter,
  householdId: string
): AugmentedCreateAccessAgeParameterInput[] {
  if (!accessParameter._hasAgeParams) return [];
  if (!accessParameter.ageParams) return [];
  if (isEmpty(accessParameter.ageParams)) return [];

  return compact(
    accessParameter.ageParams.map<AugmentedCreateAccessAgeParameterInput | null>(
      (ageParam) => {
        if (!ageParam._ageRequirementKind) return null;

        const commonAgeParams = {
          notes: ageParam.notes,
          householdID: householdId,
        };

        switch (ageParam._ageRequirementKind) {
          case 'upon':
            return {
              create: {
                ...commonAgeParams,
                ageRequirementStart: parseStringToInteger(
                  ageParam.ageRequirementStart ?? ''
                ),
              },
            };
          case 'until':
            return {
              create: {
                ...commonAgeParams,
                ageRequirementEnd: parseStringToInteger(
                  ageParam.ageRequirementEnd ?? ''
                ),
              },
            };
          case 'between':
            return {
              create: {
                ...commonAgeParams,
                ageRequirementStart: parseStringToInteger(
                  ageParam.ageRequirementStart ?? ''
                ),
                ageRequirementEnd: parseStringToInteger(
                  ageParam.ageRequirementEnd ?? ''
                ),
              },
            };
          default:
            throw new UnreachableError({
              case: ageParam._ageRequirementKind,
              message: `Unexpected age requirement kind ${ageParam._ageRequirementKind}`,
            });
        }
      }
    )
  );
}
